import React from 'react';
import { Users, HandCoins, Timer, ShieldCheck, ArrowRight, TrendingUp, Wallet, CheckCircle2, AlertTriangle, Info, Sparkles, History, DollarSign } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';

interface AjoCircle {
  id: string;
  name: string;
  contribution: number;
  members: number;
  totalPot: number;
  frequency: 'weekly' | 'monthly';
  nextPayout: string;
  myPosition: number;
  totalPositions: number;
  isJoined: boolean;
  payoutStatus: 'waiting' | 'ready' | 'received';
  autoContribute: boolean;
}

export function AjoSavingsWidget({ walletBalance, onContribute }: { walletBalance: number, onContribute: (amount: number) => void }) {
  const [circles, setCircles] = React.useState<AjoCircle[]>([
    {
      id: 'ajo-1',
      name: 'Lekki Neighbors Elite',
      contribution: 25000,
      members: 8,
      totalPot: 200000,
      frequency: 'weekly',
      nextPayout: 'Sat, Feb 21',
      myPosition: 3,
      totalPositions: 8,
      isJoined: true,
      payoutStatus: 'waiting',
      autoContribute: true
    },
    {
      id: 'ajo-2',
      name: 'Safe Hands Community',
      contribution: 5000,
      members: 12,
      totalPot: 60000,
      frequency: 'weekly',
      nextPayout: 'Wed, Feb 25',
      myPosition: 1,
      totalPositions: 12,
      isJoined: false,
      payoutStatus: 'waiting',
      autoContribute: false
    }
  ]);

  const [isProcessingPayout, setIsProcessingPayout] = React.useState(false);
  const joinedCircle = circles.find(c => c.isJoined);

  const handleToggleAuto = () => {
    if (!joinedCircle) return;
    setCircles(prev => prev.map(c => 
      c.id === joinedCircle.id ? { ...c, autoContribute: !c.autoContribute } : c
    ));
    toast.info(`Auto-Contribute ${!joinedCircle.autoContribute ? 'Enabled' : 'Disabled'}`, {
      description: !joinedCircle.autoContribute ? 'Your contribution will be deducted automatically from your wallet.' : 'You will need to manually contribute each week.'
    });
  };

  const handleManualContribution = () => {
    if (!joinedCircle) return;
    if (walletBalance < joinedCircle.contribution) {
      toast.error('Insufficient Funds', { description: 'Please top up your Sentlo Wallet to make this contribution.' });
      return;
    }
    
    // Security check simulation
    const confirmed = window.confirm(`Confirm secure contribution of ₦${joinedCircle.contribution.toLocaleString()}?`);
    if (!confirmed) return;

    onContribute(joinedCircle.contribution);
    toast.success('Contribution Successful', {
      description: `₦${joinedCircle.contribution.toLocaleString()} added to the pot for ${joinedCircle.name}.`,
      icon: <CheckCircle2 className="w-4 h-4 text-green-500" />
    });
  };

  const simulatePayout = () => {
    if (!joinedCircle) return;
    setIsProcessingPayout(true);
    
    // Simulate smart contract maturation
    setTimeout(() => {
      setCircles(prev => prev.map(c => 
        c.id === joinedCircle.id ? { ...c, payoutStatus: 'ready' } : c
      ));
      setIsProcessingPayout(false);
      toast.success('Payout Matured!', {
        description: `Your position #${joinedCircle.myPosition} is now ready for payout. ₦${joinedCircle.totalPot.toLocaleString()} is available.`,
        duration: 6000,
        icon: <Sparkles className="w-5 h-5 text-amber-500" />
      });
    }, 2000);
  };

  const handleClaimPayout = () => {
    if (!joinedCircle) return;
    toast.success('Payout Transferred', {
      description: `₦${joinedCircle.totalPot.toLocaleString()} has been added to your Sentlo Wallet.`,
      icon: <DollarSign className="w-5 h-5 text-green-500" />
    });
    setCircles(prev => prev.map(c => 
      c.id === joinedCircle.id ? { ...c, payoutStatus: 'received' } : c
    ));
    // In a real app, this would trigger an onPayoutComplete(amount) callback
  };

  return (
    <div className="bg-zinc-900 rounded-[32px] p-6 text-white overflow-hidden relative border border-zinc-800">
      <div className="absolute top-0 right-0 p-4 opacity-10">
        <HandCoins className="w-24 h-24 rotate-12" />
      </div>

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Digital Ajo Circles</h3>
              <p className="text-[9px] font-bold text-blue-400 uppercase">Trust-Based Savings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-[9px] font-black text-zinc-500 uppercase hover:text-white transition-colors">History</button>
            <div className="w-1 h-1 rounded-full bg-zinc-700" />
            <button className="text-[9px] font-black text-zinc-500 uppercase hover:text-white transition-colors">Rules</button>
          </div>
        </div>

        {joinedCircle ? (
          <div className="space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-base font-black italic uppercase text-white mb-1 leading-none">{joinedCircle.name}</h4>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-black text-zinc-500 uppercase">Cycle Progress</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                      <div key={i} className={`w-1.5 h-1.5 rounded-full ${i <= 3 ? 'bg-blue-500' : 'bg-zinc-800'}`} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-black text-zinc-500 uppercase tracking-widest mb-1">Target Payout</p>
                <p className="text-xl font-black italic text-green-400 leading-none">₦{joinedCircle.totalPot.toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white/5 rounded-2xl p-5 border border-white/5 relative overflow-hidden">
              {joinedCircle.payoutStatus === 'ready' && (
                <motion.div 
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="absolute inset-0 bg-blue-600 flex flex-col items-center justify-center text-center p-4 z-20"
                >
                  <Sparkles className="w-8 h-8 text-white mb-2 animate-bounce" />
                  <h4 className="text-sm font-black uppercase italic mb-1">Pot is Ready!</h4>
                  <p className="text-[10px] font-bold text-white/80 mb-4">Your payout of ₦{joinedCircle.totalPot.toLocaleString()} is verified.</p>
                  <button 
                    onClick={handleClaimPayout}
                    className="w-full bg-white text-blue-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all"
                  >
                    Claim To Wallet
                  </button>
                </motion.div>
              )}

              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Timer className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[10px] font-bold text-zinc-300 uppercase">Your Payout Date: <span className="text-white">Sat, Feb 21</span></span>
                </div>
                <div className="text-[9px] font-black text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-md uppercase">
                  #{joinedCircle.myPosition} in Line
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest">
                  <span className="text-zinc-500">Automation Status</span>
                  <button 
                    onClick={handleToggleAuto}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all ${joinedCircle.autoContribute ? 'text-green-400 bg-green-500/10' : 'text-zinc-400 bg-white/5'}`}
                  >
                    {joinedCircle.autoContribute ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                    {joinedCircle.autoContribute ? 'Auto-Pay Active' : 'Manual Pay'}
                  </button>
                </div>
                
                <div className="flex justify-between items-center p-3 rounded-xl bg-black/20 border border-white/5">
                  <div>
                    <p className="text-[8px] font-black text-zinc-500 uppercase mb-0.5">Next Due</p>
                    <p className="text-xs font-black uppercase tracking-tighter">₦{joinedCircle.contribution.toLocaleString()}</p>
                  </div>
                  <button 
                    onClick={handleManualContribution}
                    disabled={joinedCircle.autoContribute}
                    className={`px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${joinedCircle.autoContribute ? 'bg-zinc-800 text-zinc-500' : 'bg-white text-zinc-900 active:scale-95'}`}
                  >
                    Pay Now
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={simulatePayout}
                disabled={isProcessingPayout}
                className="bg-zinc-800 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition-all border border-zinc-700 disabled:opacity-50"
              >
                {isProcessingPayout ? <Timer className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5 text-blue-400" />}
                Check Maturation
              </button>
              <button className="bg-zinc-800 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest active:scale-95 transition-all border border-zinc-700">
                Member List
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-400" />
              <p className="text-[9px] font-bold text-blue-400 uppercase tracking-tighter leading-tight">
                Trust Score: 850 • <span className="underline cursor-pointer">View Tier Perks</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
             {/* Empty State / Join UI - Same as before but with better styling */}
             <div className="bg-white/5 rounded-2xl p-6 border border-white/5 text-center">
                <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-xs text-zinc-400 italic mb-4">You haven't joined any Ajo Circles yet. Join a neighborhood group to save securely.</p>
                <button className="w-full bg-blue-600 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 active:scale-95 transition-all">Browse Nearby Circles</button>
             </div>
          </div>
        )}
      </div>
    </div>
  );
}
